// src/components/data-table/columns/ProgressColumn.tsx
import { type ColumnDef, type Row } from '@tanstack/react-table';
import { Progress } from '@/components/ui/progress';
import { ColumnType } from '@/types/column';
import { GalleryView } from '@/types';
import { cn } from '@/lib/utils';

interface ProgressColumnOptions<T> {
  /** Column id */
  id?: string;

  /** Column accessor key */
  accessorKey?: string;

  /** Header label */
  label?: string;

  /** Given a row, return the numeric progress (0–100) or undefined */
  getProgress: (row: T) => number | undefined;

  /** Given a row, is it still uploading? */
  isUploading: (row: T) => boolean;

  galleryView: GalleryView;

  /** Optional size override (defaults to 130) */
  size?: number;

  /** Optional extra classnames */
  headerClassName?: string;
  cellClassName?: string;

  /** Optional data-cy attribute */
  dataCy?: string;
}

export const ProgressColumn = <T extends { id: string }>({
  id = ColumnType.Progress,
  accessorKey = ColumnType.Progress,
  label = 'Progress',
  getProgress,
  isUploading,
  galleryView,
  size = 130,
  cellClassName,
  headerClassName,
  dataCy,
}: ProgressColumnOptions<T>): ColumnDef<T> => ({
  id,
  accessorKey,
  header: () => label,
  size,
  meta: {
    cellClassName: cn('py-0!', cellClassName),
    ...(headerClassName && { headerClassName: headerClassName }),
  },
  cell: ({ row }: { row: Row<T> }) => {
    const progress = getProgress(row.original);
    const uploading = isUploading(row.original);

    return (
      <Progress
        value={progress}
        className={cn('h-[6px] w-full', galleryView === GalleryView.TILE && 'mt-1', !uploading && 'hidden')}
        data-cy={dataCy ?? `${galleryView ? galleryView : 'list'}-${row.index}-${ColumnType.Progress}`}
      />
    );
  },
});
